// ChildAvatar — moderation-aware identity image for a child.
//
// Concept distinction from class/activity photos:
//   * profile photo = identity image; one per child; NO moderation;
//                     parents own their child's identity.
//   * class/activity photos = the existing `child_photos` gallery,
//                             moderated by admins, never used here.
//
// This component fetches a signed URL for `children.profile_photo_path`
// (when set) and falls back to colored initials. URLs are cached in a
// module-scoped Map to avoid signing the same path on every render.

import { useEffect, useRef, useState } from "react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const SIGNED_URL_TTL = 60 * 60; // 1 hour
const cache = new Map(); // path -> { url, expiresAt }

function initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Stable colored fallback. Same name → same swatch every render.
const SWATCHES = [
  "bg-rose-200 text-rose-900",
  "bg-amber-200 text-amber-900",
  "bg-emerald-200 text-emerald-900",
  "bg-sky-200 text-sky-900",
  "bg-violet-200 text-violet-900",
  "bg-fuchsia-200 text-fuchsia-900",
];
function swatchFor(seed) {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return SWATCHES[Math.abs(h) % SWATCHES.length];
}

async function getSignedUrl(path) {
  if (!path || !isSupabaseConfigured) return null;
  const now = Date.now();
  const cached = cache.get(path);
  if (cached && cached.expiresAt > now) return cached.url;
  const { data, error } = await supabase
    .storage
    .from("child-photos")
    .createSignedUrl(path, SIGNED_URL_TTL);
  if (error) {
    cache.delete(path);
    return null;
  }
  cache.set(path, {
    url: data.signedUrl,
    expiresAt: now + (SIGNED_URL_TTL - 60) * 1000, // refresh slightly early
  });
  return data.signedUrl;
}

/** Bust the cached signed URL for one child. Call after upload/replace. */
export function invalidateChildAvatarCache(path) {
  if (path) cache.delete(path);
}

/**
 * <ChildAvatar
 *   path={child.profile_photo_path}
 *   name={`${child.first_name} ${child.last_name}`}
 *   size={40}
 *   testId={`child-avatar-${child.id}`}
 * />
 *
 * size: number (px) — square. Defaults to 40.
 */
export default function ChildAvatar({
  path = null,
  name = "",
  size = 40,
  className = "",
  testId,
}) {
  const [url, setUrl] = useState(null);
  const [errored, setErrored] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    let alive = true;
    setErrored(false);
    if (!path) {
      setUrl(null);
      return;
    }
    const myId = ++reqId.current;
    getSignedUrl(path).then((u) => {
      if (!alive || myId !== reqId.current) return;
      setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [path]);

  const px = `${size}px`;
  const fontPx = Math.max(10, Math.round(size * 0.4));

  if (path && url && !errored) {
    return (
      <img
        src={url}
        alt={name ? `${name} profile photo` : "Child profile photo"}
        data-testid={testId}
        loading="lazy"
        decoding="async"
        onError={() => setErrored(true)}
        style={{ width: px, height: px }}
        className={`rounded-full object-cover bg-neutral-100 ${className}`}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={name ? `${name} initials` : "Child initials"}
      data-testid={testId}
      style={{ width: px, height: px, fontSize: `${fontPx}px` }}
      className={`flex items-center justify-center rounded-full font-semibold ${swatchFor(name)} ${className}`}
    >
      {initials(name)}
    </span>
  );
}
